import { interpolate } from '@gig/magic-common';
import { USER_LOGGED_IN_UPDATED, LANGUAGE_UPDATED, MESSAGING_MARK_READ, MESSAGING_LIST, MESSAGING_UNREAD_LIST, MESSAGING_LOADED, LANGUAGE_QUERY, USER_LOGGED_IN_QUERY } from '@gig/magic-messages';
import { __awaiter, __generator, __assign } from 'tslib';
import { magic } from '@gig/magic-sdk';
import { NotificationDelay, notificationService, isHidden } from '@gig/magic-messaging';
import { unlocalize, MessageType, MessageStatus } from '@gig/magic-sdk/dist/adapter/messages/message';

var MessagingService = /** @class */ (function () {
    function MessagingService() {
        this.messages = [];
        this.messagesLoaded = false;
        this.registerSchemas();
        this.subscribeMessagingEvents();
        this.subscribeToMessageUpdates();
    }
    MessagingService.prototype.subscribeMessagingEvents = function () {
        var _this = this;
        window.eventBus.subscribe(USER_LOGGED_IN_UPDATED, function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.syncMessages()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); }, { replay: true });
        window.eventBus.subscribe(LANGUAGE_UPDATED, function () { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.syncMessages()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        }); });
        window.commandBus.subscribe(MESSAGING_MARK_READ, function (event) {
            _this.markAsRead(event.payload.id, event.payload.isPopOpen);
        });
    };
    MessagingService.prototype.registerQueries = function () {
        var _this = this;
        if (this.listQuery === undefined) {
            this.listQuery = window.queryBus.subscribe(MESSAGING_LIST, function () {
                if (!_this.messagesLoaded) {
                    return [];
                }
                return _this.messages;
            });
        }
        if (this.unreadListQuery === undefined) {
            this.unreadListQuery = window.queryBus.subscribe(MESSAGING_UNREAD_LIST, function () {
                if (!_this.messagesLoaded) {
                    return [];
                }
                return _this.getUnreadMessages();
            });
        }
    };
    MessagingService.prototype.unregisterQueries = function () {
        var _a, _b;
        (_a = this.listQuery) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        this.listQuery = undefined;
        (_b = this.unreadListQuery) === null || _b === void 0 ? void 0 : _b.unsubscribe();
        this.unreadListQuery = undefined;
    };
    MessagingService.prototype.syncMessages = function () {
        return __awaiter(this, void 0, void 0, function () {
            var languageFetched, _a, language, loggedIn, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        this.unregisterQueries();
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, , 6, 9]);
                        return [4 /*yield*/, Promise.all([
                                this.queryLanguage(),
                                this.queryIsLoggedIn(),
                            ])];
                    case 2:
                        _a = _c.sent(), language = _a[0], loggedIn = _a[1];
                        if (!(!language || !loggedIn)) return [3 /*break*/, 3];
                        this.clearMessages();
                        this.registerQueries();
                        return [3 /*break*/, 5];
                    case 3:
                        languageFetched = language;
                        return [4 /*yield*/, this.fetchMessages(language)];
                    case 4:
                        _c.sent();
                        _c.label = 5;
                    case 5: return [3 /*break*/, 9];
                    case 6:
                        _b = languageFetched;
                        if (!_b) return [3 /*break*/, 8];
                        return [4 /*yield*/, this.queryLanguage()];
                    case 7:
                        _b = (_c.sent()) === languageFetched;
                        _c.label = 8;
                    case 8:
                        // avoid issue where language changed while request was pending
                        if (_b) {
                            this.registerQueries();
                        }
                        return [7 /*endfinally*/];
                    case 9: return [2 /*return*/];
                }
            });
        });
    };
    MessagingService.prototype.subscribeToMessageUpdates = function () {
        var _this = this;
        magic.events.message(function (msg) { return __awaiter(_this, void 0, void 0, function () {
            var language, message, params, link, url;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.queryLanguage()];
                    case 1:
                        language = _a.sent();
                        message = unlocalize(msg, language);
                        this.updateMessage(message);
                        if ((message.type !== MessageType.NOTIFICATION &&
                            message.type !== MessageType.AD_HOC) ||
                            !message.subject) {
                            return [2 /*return*/];
                        }
                        params = __assign({}, magic.session.getUser());
                        if (message.type === MessageType.AD_HOC) {
                            link = message.link;
                            url = message.url;
                            Object.assign(params, {
                                dismissible: true,
                                closeDelay: NotificationDelay.PERMANENT,
                                label: link,
                                url: url,
                            });
                        }
                        notificationService.show(interpolate(message.subject, params), __assign({ uniqueID: message.id }, params));
                        return [2 /*return*/];
                }
            });
        }); });
    };
    MessagingService.prototype.fetchMessages = function (language) {
        return __awaiter(this, void 0, void 0, function () {
            var messages;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.publishMessagesLoaded(false);
                        return [4 /*yield*/, magic.methods.messages.get(language)];
                    case 1:
                        messages = _a.sent();
                        // latest message first
                        this.messages = messages.sort(function (msgA, msgB) {
                            return (new Date(msgB.created).getTime() -
                                new Date(msgA.created).getTime());
                        });
                        this.publishMessagesLoaded(true);
                        return [2 /*return*/];
                }
            });
        });
    };
    MessagingService.prototype.updateMessage = function (message) {
        var index = this.messages.findIndex(function (_a) {
            var id = _a.id;
            return id === message.id;
        });
        switch (message.status) {
            case MessageStatus.NEW:
                if (index > -1) {
                    return;
                }
                this.messages.unshift(message);
                this.publishMessagesLoaded(true);
                break;
            case MessageStatus.DELETED:
            case MessageStatus.CANCELLED:
                this.messages.splice(index, 1);
                this.publishMessagesLoaded(true);
                break;
            case MessageStatus.READ:
                this.markAsRead(message.id);
                break;
        }
    };
    MessagingService.prototype.clearMessages = function () {
        this.messages = [];
        this.publishMessagesLoaded(true);
    };
    MessagingService.prototype.markAsRead = function (messageId, isPopOpen) {
        var messageToRead = this.messages.find(function (_a) {
            var id = _a.id;
            return id === messageId;
        });
        if (!messageToRead) {
            return;
        }
        messageToRead.status = messageToRead.once
            ? MessageStatus.DELETED
            : MessageStatus.READ;
        if (!isPopOpen) {
            this.publishMessagesLoaded(true);
        }
    };
    MessagingService.prototype.publishMessagesLoaded = function (payload) {
        this.messagesLoaded = payload;
        window.eventBus.publish(MESSAGING_LOADED, payload);
    };
    MessagingService.prototype.getUnreadMessages = function () {
        return this.messages.filter(function (message) {
            return (message.status === MessageStatus.NEW &&
                message.type !== MessageType.NOTIFICATION &&
                !isHidden(message));
        });
    };
    MessagingService.prototype.queryLanguage = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, window.queryBus.query(LANGUAGE_QUERY)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    MessagingService.prototype.queryIsLoggedIn = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, window.queryBus.query(USER_LOGGED_IN_QUERY)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    MessagingService.prototype.registerSchemas = function () {
        window.eventBus.register(MESSAGING_LOADED, {
            type: 'boolean',
        });
        window.queryBus.register(MESSAGING_LIST, {
            type: 'array',
        });
        window.queryBus.register(MESSAGING_UNREAD_LIST, {
            type: 'array',
        });
        window.commandBus.register(MESSAGING_MARK_READ, {
            type: 'object',
            properties: {
                id: {
                    type: 'string',
                },
                isPopOpen: {
                    anyOf: [
                        {
                            type: 'boolean',
                        },
                        {
                            type: 'null',
                        },
                    ],
                },
            },
        });
    };
    return MessagingService;
}());

export { MessagingService };
